import { Component, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { MailMiddleSectionComponent } from 'src/app/private/modules/components/sales-mail-hub/mail-middle-section/mail-middle-section.component';
import { MailRightSectionComponent } from 'src/app/private/modules/components/sales-mail-hub/mail-right-section/mail-right-section.component';
import { EmailHubService } from 'src/app/private/services/email-hub.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-sales-mail-hub',
  templateUrl: './sales-mail-hub.component.html',
  styleUrls: ['./sales-mail-hub.component.scss']
})
export class SalesMailHubComponent implements OnInit {
  private readonly MAIL_HUB_SESSION_KEY = 'sales_mail_hub_mail_provider';

  @ViewChild(MailMiddleSectionComponent) childComponent!: MailMiddleSectionComponent;
  @ViewChild(MailRightSectionComponent) rightComponent!: MailRightSectionComponent;
  selectedRow: any;
  activeAction: string = 'inbox';
  sanitizedContent: any;
  isComposeOpen = false;
  loadingEmailData = false;
  isDraft = false;
  updateDraft = false;
  fetchingDraftData = false;
  currentCreatedDraftId: any;
  isAccountNotConnected = false;
  initLoading = true;
  isConnectLoading = false;
  userEmail = this.localStorage.getMailHubUserEmail();
  /** null = show Outlook vs Gmail picker */
  mailHubProvider: 'outlook' | 'gmail' | null = null;

  private gmailOAuthPollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private api: EmailHubService,
    private usersService: UsersService,
    private localStorage: LocalStorageService,
    private router: Router
  ) {
    const saved = sessionStorage.getItem(this.MAIL_HUB_SESSION_KEY) as 'outlook' | 'gmail' | null;
    if (saved === 'outlook' || saved === 'gmail') {
      this.mailHubProvider = saved;
      this.localStorage.setActiveMailboxProvider(saved);
      // initLoading stays true; ngOnInit will call API and clear it
    } else {
      this.mailHubProvider = null;
      this.initLoading = false;
    }
  }

  ngOnInit(): void {
    if (this.mailHubProvider) {
      this.checkConnectionFromApi(this.mailHubProvider);
    }
  }

  get showProviderPicker(): boolean {
    return this.mailHubProvider === null;
  }

  /** Non-null when a mailbox type is selected (safe to bind child `[mailProvider]`). */
  get hubMailProvider(): 'outlook' | 'gmail' | null {
    return this.mailHubProvider;
  }

  applyConnectionStateForProvider(): void {
    if (this.mailHubProvider === 'outlook') {
      const email = this.localStorage.getMailboxEmail('outlook');
      if (email) {
        this.isAccountNotConnected = false;
        this.userEmail = email;
      } else {
        this.isAccountNotConnected = true;
      }
    } else if (this.mailHubProvider === 'gmail') {
      const email = this.localStorage.getMailboxEmail('gmail');
      if (email) {
        this.isAccountNotConnected = false;
        this.userEmail = email;
      } else {
        this.isAccountNotConnected = true;
      }
    }
  }

  selectMailProvider(provider: 'outlook' | 'gmail'): void {
    this.mailHubProvider = provider;
    sessionStorage.setItem(this.MAIL_HUB_SESSION_KEY, provider);
    this.localStorage.setActiveMailboxProvider(provider);
    this.persistCommunicationPrefToUser();
    this.initLoading = true;
    this.checkConnectionFromApi(provider);
  }

  checkConnectionFromApi(provider: 'outlook' | 'gmail'): void {
    const payload = {
      hospital_id: this.localStorage.getHospitalId(),
      user_id: this.localStorage.getUserId()
    };
    const request$ = provider === 'outlook'
      ? this.api.getConnectedOutlookEmail(payload)
      : this.api.getConnectedGmailEmail(payload);

    request$.subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res) ? res : (res?.data ?? res?.rows ?? []);
        const connected = rows.find((r: any) => r.connected === true) ?? rows[0];
        const email: string | undefined = connected?.email;
        if (email) {
          this.localStorage.mergeMailboxConnection(provider, email);
          this.localStorage.setMailboxEmail(provider, email);
          this.userEmail = email;
          this.isAccountNotConnected = false;
        } else {
          this.isAccountNotConnected = true;
        }
        this.initLoading = false;
      },
      error: () => {
        this.isAccountNotConnected = true;
        this.initLoading = false;
      }
    });
  }

  goToMailboxPicker(): void {
    this.mailHubProvider = null;
    sessionStorage.removeItem(this.MAIL_HUB_SESSION_KEY);
    this.isAccountNotConnected = false;
    this.selectedRow = null;
    this.isComposeOpen = false;
  }

  handleConnectGmail(): void {
    this.isConnectLoading = true;
    this.api.getCodeForGoogleToken({
      hospital_id: this.localStorage.getHospitalId(),
      user_id: this.localStorage.getUserId()
    }).subscribe({
      next: (res: any) => {
        this.isConnectLoading = false;
        const url = res?.message;
        if (!url) {
          return;
        }
        const popup = window.open(url, 'GoogleConnectWindow', 'width=600,height=700,left=200,top=100');
        if (!popup) {
          return;
        }
        if (this.gmailOAuthPollTimer !== null) {
          clearInterval(this.gmailOAuthPollTimer);
        }
        this.gmailOAuthPollTimer = setInterval(() => {
          if (popup.closed) {
            if (this.gmailOAuthPollTimer !== null) {
              clearInterval(this.gmailOAuthPollTimer);
              this.gmailOAuthPollTimer = null;
            }
            this.syncGmailPreferenceAfterOAuth();
          }
        }, 400);
      },
      error: () => {
        this.isConnectLoading = false;
      }
    });
  }

  /**
   * OAuth stores tokens in the backend; Mail Hub reads provider/email from localStorage.
   * Fetch connected Gmail row(s) and update commincation_pref (same pattern as Outlook on communication settings).
   */
  private syncGmailPreferenceAfterOAuth(attempt = 0): void {
    const maxAttempts = 3;
    const retryDelayMs = 800;
    this.isConnectLoading = true;
    this.api
      .getConnectedGmailEmail({
        hospital_id: this.localStorage.getHospitalId(),
        user_id: this.localStorage.getUserId()
      })
      .subscribe({
        next: (raw: any) => {
          const rows = this.normalizeConnectedGmailResponse(raw);
          if (rows.length === 0 && attempt < maxAttempts - 1) {
            setTimeout(() => this.syncGmailPreferenceAfterOAuth(attempt + 1), retryDelayMs);
            return;
          }
          this.isConnectLoading = false;
          if (rows.length === 0) {
            window.location.reload();
            return;
          }
          const latest = rows.reduce((best: any, row: any) => {
            const t = row.connected_at ? new Date(row.connected_at).getTime() : 0;
            const bt = best.connected_at ? new Date(best.connected_at).getTime() : 0;
            return t >= bt ? row : best;
          });
          const email = latest.email;
          if (email) {
            this.localStorage.mergeMailboxConnection('gmail', email);
            this.localStorage.setMailboxEmail('gmail', email);
            this.persistCommunicationPrefToUser();
            this.applyConnectionStateForProvider();
          } else {
            window.location.reload();
          }
        },
        error: () => {
          this.isConnectLoading = false;
          window.location.reload();
        }
      });
  }

  /** Saves full v2 `commincation_pref` (both mailboxes + active) for login / other devices. */
  private persistCommunicationPrefToUser(): void {
    const userId = this.localStorage.getUserId();
    if (userId == null || userId === '') {
      return;
    }
    this.usersService
      .updateUser({
        user_id: userId,
        commincation_pref: this.localStorage.getCommPrefPayloadForUser()
      })
      .subscribe({
        error: (err) => console.error('persistCommunicationPrefToUser failed', err)
      });
  }

  private normalizeConnectedGmailResponse(raw: any): Array<{ email: string; connected_at?: string }> {
    if (raw == null) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return this.normalizeConnectedGmailResponse(JSON.parse(raw));
      } catch {
        return [];
      }
    }
    if (Array.isArray(raw?.data)) {
      return raw.data;
    }
    if (Array.isArray(raw?.body)) {
      return raw.body;
    }
    if (typeof raw?.body === 'string') {
      try {
        return this.normalizeConnectedGmailResponse(JSON.parse(raw.body));
      } catch {
        return [];
      }
    }
    return [];
  }

  navigateToSettings() {
    this.router.navigate(['/main/client-settings','communication']);
  }


  ngOnDestroy(): void {
    if (this.gmailOAuthPollTimer !== null) {
      clearInterval(this.gmailOAuthPollTimer);
      this.gmailOAuthPollTimer = null;
    }

    console.log('SalesMailHubComponent is being destroyed');

    if (this.isDraft) {
      console.log('Saving draft before exit...');
      // Call a service or method to save the draft
      this.saveDraft();
    }
  }

  handleAccountNotConnected(event: any) {
    this.initLoading = false;
    if(event){
      this.isAccountNotConnected = true;
    }else{
      this.isAccountNotConnected = false;
    }
  }

  saveDraft() {
    // Logic to save the draft email
    console.log('Draft saved.');


    let attachments: any[] = [];
    this.rightComponent.files.forEach((file) => {
      attachments.push({
        "@odata.type": "#microsoft.graph.fileAttachment",
        "name": file.file.name,
        "contentType": file.file.type,
        "contentBytes": file.preview?.split(',')[1]
      });
    });

    this.isDraft = false;

    if (this.mailHubProvider === 'gmail') {
      const subj = this.rightComponent.subject || '(no subject)';
      const bodyHtml = this.rightComponent.editorData || '<p><br></p>';
      const toStr =
        this.rightComponent.emails?.length > 0
          ? this.rightComponent.emails.join(',')
          : this.rightComponent.emailInput || this.localStorage.getMailHubUserEmail();
      const ccStr =
        this.rightComponent.ccEmails?.length > 0
          ? this.rightComponent.ccEmails.join(',')
          : this.rightComponent.ccInput || '';
      const gmailAtt = this.buildGmailAttachmentsFromFiles(this.rightComponent.files);
      this.rightComponent.clearData();
      this.api
        .createGmailDraftEmail({
          hospital_id: this.localStorage.getHospitalId(),
          user_email: this.localStorage.getMailHubUserEmail(),
          to: toStr,
          cc: ccStr,
          subject: subj,
          body: bodyHtml,
          ...(gmailAtt.length ? { attachments: gmailAtt } : {})
        })
        .subscribe({
          next: (res: any) => {
            this.currentCreatedDraftId = res?.id;
            if (this.activeAction === 'drafts') {
              this.handleUpdateData2();
            }
          },
          error: (err: any) => {
            console.error('Error saving Gmail draft:', err);
          }
        });
      return;
    }

    const param = {
      subject: this.rightComponent.subject,
      contentType: 'HTML',
      bodyContent: this.rightComponent.editorData,
      recipientEmails: this.rightComponent.emails?.length > 0 ? this.rightComponent.emails : [this.rightComponent.emailInput],
      fromEmail: this.localStorage.getMailHubUserEmail(),
      ccRecipients: this.rightComponent.ccEmails?.length > 0 ? this.rightComponent.ccEmails : this.rightComponent.ccInput ? [this.rightComponent.ccInput] : [],
      attachments: attachments,
      hospital_id: this.localStorage.getHospitalId()
    }

    this.rightComponent.clearData();

    console.log('Draft email param:', param);
    this.api.createOutlookDraftEmail(param).subscribe({
      next: (res: any) => {
        this.currentCreatedDraftId = res.id;
        console.log('Draft saved successfully:', res);
        if (this.activeAction === 'drafts') {
          this.handleUpdateData2();
        }
      },
      error: (err: any) => {
        console.error('Error saving draft:', err);
      }
    });
  }

  /** Base64 attachment payloads for part-d `buildRawMessage` / `resolveAttachments`. */
  private buildGmailAttachmentsFromFiles(files: any[] | undefined): Array<{ name: string; contentType: string; data: string }> {
    if (!files?.length) {
      return [];
    }
    return files
      .map((f) => ({
        name: f.file?.name || 'attachment',
        contentType: f.file?.type || 'application/octet-stream',
        data: f.preview?.split(',')[1] || ''
      }))
      .filter((a) => a.data);
  }

  private patchGmailDraft(draftId: string | undefined, after?: () => void): void {
    if (!draftId) {
      return;
    }
    const subj = this.rightComponent.subject || '(no subject)';
    const bodyHtml = this.rightComponent.editorData || '<p><br></p>';
    const toStr =
      this.rightComponent.emails?.length > 0
        ? this.rightComponent.emails.join(',')
        : this.rightComponent.emailInput || this.localStorage.getMailHubUserEmail();
    const ccStr =
      this.rightComponent.ccEmails?.length > 0
        ? this.rightComponent.ccEmails.join(',')
        : this.rightComponent.ccInput || '';
    const attachments = this.buildGmailAttachmentsFromFiles(this.rightComponent?.files);
    this.rightComponent.clearData();
    this.api
      .updateGmailDraftEmail({
        hospital_id: this.localStorage.getHospitalId(),
        user_email: this.localStorage.getMailHubUserEmail(),
        draft_id: draftId,
        to: toStr,
        cc: ccStr,
        subject: subj,
        body: bodyHtml,
        ...(attachments.length ? { attachments } : {})
      })
      .subscribe({
        next: () => {
          this.updateDraft = false;
          after?.();
          this.handleUpdateData2();
        },
        error: (err: any) => {
          console.error('Error updating Gmail draft:', err);
        }
      });
  }

  handleUpdateDraft() {
    // Logic to update the draft email
    console.log('Draft updated.');

    if (this.mailHubProvider === 'gmail') {
      const draftId =
        this.selectedRow?.gmailDraftId ||
        this.currentCreatedDraftId ||
        this.selectedRow?.id;
      this.patchGmailDraft(draftId);
      return;
    }

    const param = {
      subject: this.rightComponent.subject,
      contentType: 'HTML',
      bodyContent: this.rightComponent.editorData,
      recipientEmails: this.rightComponent.emails?.length > 0 ? this.rightComponent.emails : [this.rightComponent.emailInput],
      fromEmail: this.localStorage.getMailHubUserEmail(),
      ccRecipients: this.rightComponent.ccEmails?.length > 0 ? this.rightComponent.ccEmails : this.rightComponent.ccInput ? [this.rightComponent.ccInput] : [],
      // attachments: this.rightComponent.files,
      hospital_id: this.localStorage.getHospitalId(),
      messageId: this.selectedRow?.id,
      draft_message_id: this.selectedRow?.id ? this.selectedRow?.id : this.currentCreatedDraftId
    }

    this.rightComponent.clearData();

    if (this.rightComponent.files.length > 0) {

    }


    console.log('Draft email param:', param);
    this.api.updateOutlookDraftEmail(param).subscribe({
      next: (res: any) => {
        console.log('Draft updated successfully:', res);
        this.updateDraft = false;
        this.handleUpdateData2();
      },
      error: (err: any) => {
        console.error('Error updating draft:', err);
      }
    });
  }

  handleUpdateDraft2(param: any) {
    console.log('Draft updated.');

    if (this.mailHubProvider === 'gmail') {
      const draftId =
        param?.draft_message_id || this.currentCreatedDraftId || this.selectedRow?.gmailDraftId;
      this.patchGmailDraft(draftId);
      return;
    }

    this.rightComponent.clearData();

    console.log('Draft email param:', param);
    this.api.updateOutlookDraftEmail(param).subscribe({
      next: (res: any) => {
        console.log('Draft updated successfully:', res);
        this.updateDraft = false;
        this.handleUpdateData2();
      },
      error: (err: any) => {
        console.error('Error updating draft:', err);
      }
    });
  }

  handleUpdateDraft3() {
    console.log('Draft updated.');

    if (this.mailHubProvider === 'gmail') {
      this.patchGmailDraft(this.currentCreatedDraftId);
      return;
    }

    const param = {
      subject: this.rightComponent.subject,
      contentType: 'HTML',
      bodyContent: this.rightComponent.editorData,
      recipientEmails: this.rightComponent.emails?.length > 0 ? this.rightComponent.emails : [this.rightComponent.emailInput],
      fromEmail: this.localStorage.getMailHubUserEmail(),
      ccRecipients: this.rightComponent.ccEmails?.length > 0 ? this.rightComponent.ccEmails : this.rightComponent.ccInput ? [this.rightComponent.ccInput] : [],
      // attachments: this.rightComponent.files,
      hospital_id: this.localStorage.getHospitalId(),
      draft_message_id: this.currentCreatedDraftId
    }

    this.rightComponent.clearData();


    console.log('Draft email param:', param);
    this.api.updateOutlookDraftEmail(param).subscribe({
      next: (res: any) => {
        console.log('Draft updated successfully:', res);
        this.updateDraft = false;
        this.handleUpdateData2();
      },
      error: (err: any) => {
        console.error('Error updating draft:', err);
      }
    });
  }


  onActionChange(action: string) {

    if (this.loadingEmailData) {
      return;
    }

    this.activeAction = action;
  }

  handleUpdateData() {
    console.log('Refetching data...');
    this.childComponent.refetch();
  }

  handleUpdateData2() {
    console.log('Refetching data...');
    this.childComponent.refetch();
  }




  handleSelectRow(row: any) {
    this.isComposeOpen = false;

    if (this.isDraft) {
      this.handleUpdateDraft();

      this.isDraft = false;
    }

    if (this.updateDraft) {
      this.handleUpdateDraft();
      this.updateDraft = false;
    }

    console.log('Selected row:', row);
    this.selectedRow = row;

    if (this.activeAction === 'drafts') {
      this.fetchingDraftData = true;
    } else {
      this.fetchingDraftData = false;
    }



    try {
      this.loadingEmailData = true;

      if (this.mailHubProvider === 'gmail') {
        this.api
          .getGmailEmailWithAttachment({
            hospital_id: this.localStorage.getHospitalId(),
            user_email: this.localStorage.getMailHubUserEmail(),
            message_id: this.selectedRow?.id
          })
          .subscribe({
            next: (res: any) => {
              this.rightComponent.attachments = (res?.attachments || []).map((a: any) => ({
                id: a.id,
                name: a.filename,
                contentType: a.mimeType
              }));
              const html = res?.body || '';
              this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(html);
              this.loadingEmailData = false;
              if (this.activeAction === 'drafts') {
                this.isComposeOpen = true;
                this.isDraft = false;
                this.updateDraft = true;
                this.rightComponent.fillData(this.selectedRow, html);
              }
            },
            error: (err: any) => {
              this.loadingEmailData = false;
              console.error('Error fetching Gmail message:', err);
            }
          });
        return;
      }

      const payload = {
        hospital_id: this.localStorage.getHospitalId(),
        user_email: this.localStorage.getMailHubUserEmail(),
        messageId: this.selectedRow?.id
      };

      const payload2 = {
        fromEmail: this.localStorage.getMailHubUserEmail(),
        hospital_id: this.localStorage.getHospitalId(),
        message_id: this.selectedRow?.id
      };

      this.api.getOutlookEmailAttachments(payload2).subscribe({
        next: (res: any) => {
          console.log('Email attachments:', res);
          this.rightComponent.attachments = res?.value;
          console.log('Email attachments set successfully.');
        },
        error: (err: any) => {
          console.error('Error fetching email attachments:', err);
        }
      });

      // Fetch email with attachment
      this.api.getOutlookEmailWithAttachment(payload).subscribe({
        next: (res: any) => {
          // Sanitize and trust the email content
          console.log('Email content:', res);
          this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(res);
          console.log('Email content sanitized and set successfully.');
          this.loadingEmailData = false

          if (this.activeAction === 'drafts') {
            this.isComposeOpen = true;
            this.isDraft = false;
            this.updateDraft = true;
            this.rightComponent.fillData(this.selectedRow, res);

            return;
          }

        },
        error: (err: any) => {
          // Log and handle API errors
          this.loadingEmailData = false;
          console.error('Error fetching email with attachment:', err);
        }
      });
    } catch (e) {
      console.error('Error in handleSelectRow:', e);
    }
  }

  handleCompose() {
    this.isComposeOpen = true;
    this.isDraft = true;
    this.saveDraft();
  }

  handleComposeClose() {

    if (this.currentCreatedDraftId) {
      if (this.rightComponent.files.length > 0) {
        this.handleUpdateDraftandAddAttachment();
      } else if (this.isDraft) {
        this.handleUpdateDraft3();
      } else {
        this.handleUpdateDraft();
      }
      this.currentCreatedDraftId = null;
    }

    // if(this.isDraft) {
    //   this.saveDraft();
    // }

    if (this.updateDraft) {
      if (this.rightComponent.files.length > 0) {
        this.handleUpdateDraftandAddAttachment();
      } else {
        this.handleUpdateDraft();

      }
    }


    this.isComposeOpen = false;
    this.isDraft = false;
    this.updateDraft = false;
    this.selectedRow = null;
    this.fetchingDraftData = false;
  }

  handleSaveImageandUpdateDraft() {
    const gmailDraftId =
      this.mailHubProvider === 'gmail'
        ? this.rightComponent.replyId ||
          this.currentCreatedDraftId ||
          this.selectedRow?.gmailDraftId ||
          this.selectedRow?.id
        : null;
    const outlookMessageId = this.selectedRow?.id;

    this.isComposeOpen = false;
    this.isDraft = false;
    this.updateDraft = false;
    this.selectedRow = null;
    this.fetchingDraftData = false;

    if (this.mailHubProvider === 'gmail') {
      this.patchGmailDraft(gmailDraftId || undefined);
      return;
    }

    const attachments = this.rightComponent?.files.map((file) => ({
      "@odata.type": "#microsoft.graph.fileAttachment",
      "name": file.file.name,
      "contentType": file.file.type,
      "contentBytes": file.preview?.split(',')[1]
    }));

    const attachmentPayload = {
      fromEmail: this.localStorage.getMailHubUserEmail(),
      draft_id: this.rightComponent.replyId,
      hospital_id: this.localStorage.getHospitalId(),
      attachments: attachments
    };

    const param = {
      subject: this.rightComponent.subject,
      contentType: 'HTML',
      bodyContent: this.rightComponent.editorData,
      recipientEmails: this.rightComponent.emails?.length > 0 ? this.rightComponent.emails : [this.rightComponent.emailInput],
      fromEmail: this.localStorage.getMailHubUserEmail(),
      ccRecipients: this.rightComponent.ccEmails?.length > 0 ? this.rightComponent.ccEmails : this.rightComponent.ccInput ? [this.rightComponent.ccInput] : [],
      // attachments: this.rightComponent.files,
      hospital_id: this.localStorage.getHospitalId(),
      messageId: outlookMessageId,
      draft_message_id: this.rightComponent.replyId
    };


    // Call addAttachmentInOutlookReply first, then handleUpdateDraft
    this.api.addAttachmentInOutlookReply(attachmentPayload).subscribe({
      next: () => {
        this.handleUpdateDraft2(param)
      },
      error: (error) => {
        this.rightComponent.handleError(error, 'Error adding attachments');
      }
    });
  }

  handleMakeIsDraftFalse() {
    this.isDraft = false;
    this.updateDraft = false;
    this.currentCreatedDraftId = null;
  }

  handleMakeIsDraftTrue() {
    this.isDraft = true;
  }


  handleUpdateDraftandAddAttachment() {
    console.log('Updating draft and adding attachment...');

    if (this.mailHubProvider === 'gmail') {
      const draftId =
        this.currentCreatedDraftId ||
        this.selectedRow?.gmailDraftId ||
        this.selectedRow?.id;
      this.patchGmailDraft(draftId);
      return;
    }

    const attachments = this.rightComponent?.files.map((file) => ({
      "@odata.type": "#microsoft.graph.fileAttachment",
      "name": file.file.name,
      "contentType": file.file.type,
      "contentBytes": file.preview?.split(',')[1]
    }));

    const attachmentPayload = {
      fromEmail: this.localStorage.getMailHubUserEmail(),
      draft_id: this.currentCreatedDraftId ? this.currentCreatedDraftId : this.selectedRow?.id,
      hospital_id: this.localStorage.getHospitalId(),
      attachments: attachments
    };

    const param = {
      subject: this.rightComponent.subject,
      contentType: 'HTML',
      bodyContent: this.rightComponent.editorData,
      recipientEmails: this.rightComponent.emails?.length > 0 ? this.rightComponent.emails : [this.rightComponent.emailInput],
      fromEmail: this.localStorage.getMailHubUserEmail(),
      ccRecipients: this.rightComponent.ccEmails?.length > 0 ? this.rightComponent.ccEmails : this.rightComponent.ccInput ? [this.rightComponent.ccInput] : [],
      // attachments: this.rightComponent.files,
      hospital_id: this.localStorage.getHospitalId(),
      messageId: this.selectedRow?.id,
      draft_message_id: this.currentCreatedDraftId ? this.currentCreatedDraftId : this.selectedRow?.id
    }

    // Call addAttachmentInOutlookDraft first, then handleUpdateDraft
    this.api.addAttachmentInOutlookReply(attachmentPayload).subscribe({
      next: () => {

      },
      error: (error) => {
        this.rightComponent.handleError(error, 'Error adding attachments');
      }
    });

    this.handleUpdateDraft2(param);
  }

  handleAccountAlreadyConnected() {

  }

  handleConnectAccount() {
    this.isConnectLoading = true;

    const payload = {
      hospital_id: this.localStorage.getHospitalId(),
      email:  this.localStorage.getMailHubUserEmail()
    };

    this.api.getCodeForOutlookToken(payload).subscribe({
      next: (res: any) => {
        this.isConnectLoading = false;
        const url = res?.message;
        if (url) {
          // Open popup with specific size
          const popup = window.open(
            url,
            'OutlookConnectWindow',
            'width=600,height=700,left=200,top=100'
          );

          if (popup) {
            // Polling to check if popup is closed
            const timer = setInterval(() => {
              if (popup.closed) {
                clearInterval(timer);
                console.log('Popup window closed');
                this.onPopupClosed();  // 🔁 Call your desired function here
              }
            }, 500);
          } else {
            console.error('Failed to open popup window. It might be blocked by the browser.');
          }
        } else {
          console.error('No URL found in response:', res);
        }
      },
      error: (err: any) => {
        this.isConnectLoading = false;
        console.error('Error fetching code for Outlook token:', err);
      }
    });
  }

  // 🧩 Callback function to run when popup closes
  onPopupClosed() {
    this.initLoading = true;
    this.isAccountNotConnected = false;
    window.location.reload();
    // this.childComponent.refetch();
  }

}
